import React, { useCallback, useState } from 'react';
import {
  businessCustomerDetailsSchema,
  dropshipValidationSchema,
  privateCustomerAddressSchema,
  privateCustomerAlternativeAddressSchema,
} from './utils/Checkout.ValidationSchema';
import { useTypedSelector } from '../../Hooks/useTypedSelector';
import { debounce } from 'lodash-es';
import {
  saveCustomerDetail,
  setValidationErrors,
} from '../../Actions/Checkout.action';
import { translate } from '../../Services/translation';
import { useTypedDispatch } from '../../Hooks/useTypedDispatch';
import { ValidationError } from 'yup';
import { triggerToast } from '../../Services/Toast.service';
import LoadingSpinner from '../LoadingSpinner';

const CheckoutConfirmCustomerDetailsButton = () => {
  const dispatch = useTypedDispatch();
  const { validationErrors } = useTypedSelector((state) => state.checkout);
  const {
    customerDetails,
    isLoadingCustomerDetails,
    isBusinessCustomer,
    selectedDeliveryAddressId,
    useDropshipping,
    alternativeAddress,
    showAlternativeAddress,
    deliveryMethods,
    shippingConfirmed,
    dropshipCustomerDetails,
    selectedDropshipAddressType,
  } = useTypedSelector((state) => state.checkout.payload);
  const [saveDetailsAbortController, setSaveDetailsAbortController] =
    useState<AbortController | null>();

  const saveDetails = useCallback(
    debounce(() => {
      let abortController = new AbortController();
      if (saveDetailsAbortController) {
        saveDetailsAbortController.abort();
      }
      setSaveDetailsAbortController(abortController);
      dispatch(saveCustomerDetail(abortController));
    }, 300),
    [saveDetailsAbortController]
  );

  const onConfirmCustomerDetailsClick = async (e) => {
    e.preventDefault();
    let hasErrors = false;
    let errors = {};

    try {
      if (isBusinessCustomer) {
        const schema = useDropshipping
          ? dropshipValidationSchema
          : businessCustomerDetailsSchema;
        await schema.validate(
          {
            ...(useDropshipping
              ? dropshipCustomerDetails
              : customerDetails),
            selectedDeliveryAddressId,
            selectedDropshipAddressType,
            useDropshipping,
          },
          { abortEarly: false }
        );
      } else {
        await privateCustomerAddressSchema.validate(customerDetails, {
          abortEarly: false,
        });
      }
    } catch (err) {
      hasErrors = true;
      errors = onValidationError(
        err,
        useDropshipping ? 'dropshipCustomerDetails' : 'customerDetails'
      );

      // Trigger toasts for validation errors if B2B
      if (err instanceof ValidationError) {
        let validationErr = err as ValidationError;
        if (isBusinessCustomer) {
          triggerToast({
            title: translate(`general.error`),
            text: 'Validation error, contact customer support',
            type: 'error',
            autoClose: false
          });
          for (let error of validationErr.inner) {
            console.error({
              title: translate(`checkout.error.${error.path}`),
              text: error.message,
              type: 'error',
            });
          }
        }
      }
    }

    if (!isBusinessCustomer && showAlternativeAddress) {
      try {
        await privateCustomerAlternativeAddressSchema.validate(
          alternativeAddress ?? {},
          { abortEarly: false }
        );
      } catch (err) {
        hasErrors = true;
        onValidationError(err, 'alternativeAddress', errors);
      }
    }

    if (!hasErrors) {
      saveDetails();
    }
  };

  const onValidationError = (
    err: ValidationError,
    stateKey: string,
    existingErrors = {}
  ) => {
    const errors = err.inner.reduce(
      (prev, i) => ({
        ...prev,
        [`${stateKey}-${i.path}`]: i.errors,
      }),
      {}
    );

    dispatch(setValidationErrors({ ...existingErrors, ...errors }));
    return errors;
  };

  return (
    <button
      className="btn btn--black btn--xlarge col-span-2 mt-4 w-full"
      onClick={onConfirmCustomerDetailsClick}
      disabled={isLoadingCustomerDetails}
    >
      {!isLoadingCustomerDetails &&
        translate(
          deliveryMethods.length == 1 && shippingConfirmed
            ? 'general.confirm'
            : 'checkout.proceed-to-shipping'
        )}
      {isLoadingCustomerDetails && <LoadingSpinner />}
    </button>
  );
};

export default CheckoutConfirmCustomerDetailsButton;
